import { tracked } from '@glimmer/tracking';
import type MappingCategoryBenchmark from 'lumen/utils/datamodels/mapping-category-benchmark';

type ApiPmsMappingValueBenchmark = {
  category_id: string;
  pms_value: string;
  read_only: boolean;
  read_only_reason: string;
  room_nights: number;
  suggested_category_id: string;
  suggested_category_reason: string;
};

export default class PmsMappingValueBenchmark {
  // Reference to the category based on the categoryId
  @tracked category: MappingCategoryBenchmark | null = null;
  // Track categoryId to make sure rowsFiltered length updates when categoryId's change.
  @tracked categoryId: string | null = null;
  @tracked isSelected = false;
  @tracked readOnly = false;
  /*
    If the value is read only, it will have a readOnlyReason object
    The readOnlyReason object has a reason 'code' and a reason 'arg'
    code => 'chain_mapping' or 'recently_changed'
  */
  @tracked readOnlyReason = {};
  @tracked roomNights: number | null = null;
  @tracked suggestedCategoryId = {};

  pmsValue: string;
  suggestedCategoryReason: string;

  // eslint-disable-next-line max-params
  constructor(
    categoryId: string,
    pmsValue: string,
    roomNights: number,
    readOnly: boolean,
    readOnlyReason: string,
    suggestedCategoryId: string,
    suggestedCategoryReason: string,
  ) {
    this.categoryId = categoryId;
    this.pmsValue = pmsValue;
    this.roomNights = roomNights;
    this.readOnly = readOnly;
    this.readOnlyReason = readOnlyReason;
    this.suggestedCategoryId = suggestedCategoryId;
    this.suggestedCategoryReason = suggestedCategoryReason;
  }

  static findCategoryById(
    categories: MappingCategoryBenchmark[],
    id: string,
  ): MappingCategoryBenchmark | null {
    // ID would be e.g. 'transient__discount__direct' meaning Transient > Discount > Direct
    let foundOption = null;

    const path = id.split('__');
    const lastId = path[path.length - 1];

    // Use a for loop so we can break out of it as soon as we find the category.
    for (let i = 0; i < categories.length; i++) {
      const category = categories[i];

      if (category) {
        if (category.id === lastId || category.id === id) {
          foundOption = categories[i];
        } else if (category.children) {
          foundOption = PmsMappingValueBenchmark.findCategoryById(
            category.children,
            id,
          );
        }
      }

      if (foundOption) {
        return foundOption;
      }
    }

    return foundOption ?? null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  static fromApiResponse(rowItem: ApiPmsMappingValueBenchmark) {
    return new PmsMappingValueBenchmark(
      rowItem.category_id,
      rowItem.pms_value,
      rowItem.room_nights,
      rowItem.read_only,
      rowItem.read_only_reason,
      rowItem.suggested_category_id,
      rowItem.suggested_category_reason,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  toApiPatchFormat() {
    return {
      pms_value: this.pmsValue,
      category_id: this.categoryId,
    };
  }

  updateCategoryRef(rootCategory: MappingCategoryBenchmark): void {
    if (this.categoryId) {
      this.category = PmsMappingValueBenchmark.findCategoryById(
        rootCategory.children,
        this.categoryId,
      );
    }
  }
}
